import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Header, Footer } from '@/components/PublicShell';
import { Button } from '@/components/ui/button';
import { AnimalSilhouette } from '@/components/AnimalSilhouette';
import { StateBadge } from '@/components/StateBadge';
import { EnquiryDialog } from '@/components/EnquiryDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { DETAIL } from '@/constants/testIds';
import { ChevronLeft, Share2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AnimalDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [animal, setAnimal] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: a }, { data: s }] = await Promise.all([
        api.get(`/public/animals/${slug}`),
        api.get('/public/settings'),
      ]);
      setAnimal(a);
      setSettings(s);
      const siteUrl = (s?.site_url || 'https://tollyhorses.com').replace(/\/$/, '');
      const canonicalPath = `/animal/${a.slug || a.id}`;
      document.title = `Sponsor ${a.name} · Tollygunge Club`;

      // Canonical
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
      link.setAttribute('href', `${siteUrl}${canonicalPath}`);

      const setMeta = (attr, key, content) => {
        let m = document.querySelector(`meta[${attr}='${key}']`);
        if (!m) { m = document.createElement('meta'); m.setAttribute(attr, key); document.head.appendChild(m); }
        m.setAttribute('content', content);
      };
      const desc = a.one_liner || `Sponsor ${a.name} at the Tollygunge Club Equestrian Centre, Kolkata.`;
      setMeta('name', 'description', desc);
      setMeta('property', 'og:type', 'article');
      setMeta('property', 'og:site_name', 'Tollygunge Club Equestrian Centre');
      setMeta('property', 'og:title', `Sponsor ${a.name} · Tollygunge Club`);
      setMeta('property', 'og:description', desc);
      setMeta('property', 'og:url', `${siteUrl}${canonicalPath}`);
      const cover = (a.gallery && a.gallery.length > 0) ? a.gallery[a.cover_index || 0] : null;
      const ogImage = cover || `${siteUrl}/images/hero-horses-1200.jpg`;
      setMeta('property', 'og:image', ogImage);
      setMeta('property', 'og:image:alt', `${a.name} — ${a.category === 'horse' ? 'horse' : 'pony'} at the Tollygunge Club Equestrian Centre`);
      setMeta('name', 'twitter:card', 'summary_large_image');
      setMeta('name', 'twitter:title', `Sponsor ${a.name} · Tollygunge Club`);
      setMeta('name', 'twitter:description', desc);
      setMeta('name', 'twitter:image', ogImage);
    } catch (e) {
      console.error('Failed to load animal detail:', e);
      setNotFound(true);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const share = async () => {
    const siteUrl = (settings?.site_url || 'https://tollyhorses.com').replace(/\/$/, '');
    const url = `${siteUrl}/animal/${animal?.slug || animal?.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Sponsor ${animal?.name}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied.');
      }
    } catch (e) {
      // AbortError is emitted when the user cancels the share/copy dialog — expected, ignore.
      if (e?.name !== 'AbortError') {
        console.warn('Share failed:', e);
      }
    }
  };

  if (notFound) {
    return (
      <div>
        <Header />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-24 text-center">
          <h1 className="text-3xl font-extrabold">Animal not found</h1>
          <p className="text-[#3E4A44] mt-2">This link may have moved or been removed.</p>
          <Button className="btn mt-6 rounded-lg h-12 bg-[#1E3A2F] hover:bg-[#16302A] font-bold" onClick={() => nav('/')}>Back to the horses</Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!animal) {
    return (
      <div>
        <Header />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10">
          <Skeleton className="aspect-[4/3] w-full max-w-2xl mx-auto rounded-xl" />
        </div>
        <Footer />
      </div>
    );
  }

  const isAvailable = animal.state === 'available';
  const isReserved = animal.state === 'reserved';
  const isSponsored = animal.state === 'sponsored';
  const gallery = animal.gallery || [];

  return (
    <div data-testid={DETAIL.page} className="pb-24 md:pb-0">
      <Header />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 sm:py-14">
        <Link to="/" data-testid={DETAIL.backButton} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1E3A2F] hover:opacity-80 mb-6 min-h-[40px]">
          <ChevronLeft className="h-4 w-4" />
          Back to the herd
        </Link>
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-3">
            <div className="relative rounded-xl bg-white border border-border overflow-hidden card-shadow">
              {gallery.length > 0 ? (
                <Carousel className="w-full">
                  <CarouselContent>
                    {gallery.map((src, i) => (
                      <CarouselItem key={src}>
                        <div className="aspect-[4/3]">
                          <img src={src} alt={`${animal.name} — photo ${i + 1}`} className="w-full h-full object-cover" />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {gallery.length > 1 && (<><CarouselPrevious /><CarouselNext /></>)}
                </Carousel>
              ) : (
                <div className="aspect-[4/3]">
                  <AnimalSilhouette category={animal.category} />
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 flex-wrap">
              <StateBadge state={animal.state} />
              <span className="text-xs font-bold text-muted-foreground">{animal.category === 'horse' ? 'Horse' : 'Pony'}</span>
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">{animal.name}</h1>
            {animal.one_liner && <p className="mt-3 text-lg text-[#3E4A44] leading-relaxed">{animal.one_liner}</p>}
            <div className="mt-6 space-y-2 text-[15px]">
              {animal.breed && <p><span className="font-bold text-muted-foreground mr-2">Breed</span>{animal.breed}</p>}
              {animal.age && <p><span className="font-bold text-muted-foreground mr-2">Age</span>{animal.age}</p>}
              {(animal.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {animal.tags.map((t) => (
                    <span key={t} className="rounded-full bg-[#F7F8F7] border border-border px-3 py-1 text-xs font-semibold text-[#3E4A44]">{t}</span>
                  ))}
                </div>
              )}
            </div>
            {animal.story && (
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-[16px] leading-relaxed whitespace-pre-line text-[#22322B]">{animal.story}</p>
              </div>
            )}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {isAvailable && (
                <Button
                  data-testid={DETAIL.sponsorButton}
                  onClick={() => setDialogOpen(true)}
                  className="btn rounded-lg bg-[#1E3A2F] hover:bg-[#16302A] h-12 px-6 font-bold text-white"
                >
                  Sponsor {animal.name}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              )}
              {isReserved && (
                <div data-testid={DETAIL.sponsorButton} className="inline-flex items-center h-12 text-[14px] font-semibold text-[#6B7370]">Currently on hold</div>
              )}
              {isSponsored && (
                <Button data-testid={DETAIL.sponsorButton} variant="outline" className="btn rounded-lg h-12 font-bold" onClick={() => nav('/#the-herd')}>Meet the rest of the herd</Button>
              )}
              <Button data-testid={DETAIL.shareButton} variant="outline" onClick={share} className="btn rounded-lg h-12 font-bold">
                <Share2 className="mr-1.5 h-4 w-4" />
                Share
              </Button>
            </div>
            {isSponsored && animal.sponsor_name && (
              <p className="mt-4 text-sm font-semibold text-[#384540]">Sponsored by {animal.sponsor_name}</p>
            )}
          </div>
        </div>
      </main>
      <Footer address={settings?.footer_address} established={settings?.footer_est} />

      {isAvailable && (
        <div className="sticky-cta md:hidden">
          <Button onClick={() => setDialogOpen(true)} className="btn w-full rounded-lg bg-[#1E3A2F] hover:bg-[#16302A] h-12 text-white font-bold">
            Sponsor {animal.name}
          </Button>
        </div>
      )}

      <EnquiryDialog
        animal={animal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={load}
        formIntro={settings?.form_intro}
        successTemplate={settings?.success_message_template}
      />
    </div>
  );
}
